<!-- eslint-disable max-len -->
<template>
  <v-container class="px-6 px-sm-12">
    <PageHeader :header-text="'Voyage Reports'" :header-icon="'mdi-ferry'" />

    <!-- Title -->
    <div class="d-flex mb-4">
      <span class="headline primary--text font-weight-medium" v-text="title" />
      <v-spacer></v-spacer>
      <v-dialog v-model="dialog" max-width="850px">
        <template v-slot:activator="{ on }">
          <v-btn color="primary" v-on="on">
            <v-icon left>mdi-plus</v-icon>Add
          </v-btn>
        </template>
        <v-card>
          <v-card-text>
            <VoyageReportForm
              :report="report"
              :onSubmitClick="submitVoyageReport"
              :onCloseClick="close"
              :isSubmitting="isSubmitting"
              :bus="bus"
            />
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>

    <v-tabs
      v-model="tab"
      :right="!centerTabs"
      :centered="centerTabs"
      :fixed-tabs="centerTabs"
      background-color="background"
    >
      <v-tab href="#active">
        <v-icon> mdi-view-list </v-icon>
      </v-tab>
      <v-tab href="#all">
        <v-icon> mdi-table </v-icon>
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" class="background">
      <v-tab-item value="active">
        <v-progress-circular
          v-if="voyageReportsLoading"
          color="primary"
          indeterminate
        />

        <v-row
          v-else-if="activeVoyageReports && activeVoyageReports.length > 0"
        >
          <v-col
            cols="12"
            v-for="voyage in activeVoyageReports"
            :key="voyage.id ? voyage.id : voyage.idc"
          >
            <VoyageListCard :voyage="voyage" />
          </v-col>
        </v-row>

        <v-row v-else-if="!voyageReportsLoading" justify="center">
          <InfoAlert message="No active voyages found for site" />
        </v-row>
      </v-tab-item>

      <!-- All Voyages Tab -->
      <v-tab-item value="all">
        <v-data-table
          class="elevation-1"
          style="cursor: pointer"
          :items="voyageReports"
          :headers="headers"
          :items-per-page="15"
          mobile-breakpoint="930"
          :search="search"
          :item-class="customRowClass"
          :loading="voyageReportsLoading"
          @click:row="goToVoyageDetails"
        >
          <template v-slot:top>
            <div class="d-flex pa-4">
              <v-icon @click="showSearch = !showSearch">mdi-magnify</v-icon>
              <v-slide-x-transition>
                <v-text-field
                  v-show="showSearch"
                  class="ma-0 pa-0"
                  v-model="search"
                  label="Search"
                  single-line
                  hide-details
                  clearable
                ></v-text-field>
              </v-slide-x-transition>
              <v-spacer />
            </div>
          </template>
          <template v-slot:item.voyageStartDateTimeLt="{ item }">
            <span v-if="item.voyageStartDateTimeLt">{{
              item.voyageStartDateTimeLt | removeTAndSecondsAndFormatDate
            }}</span>
            <span v-else>Pending</span>
          </template>
          <template v-slot:item.status="{ item }">
            <div class="d-flex flex-row justify-space-between">
              <v-chip
                :color="getStatusColour(item.status)"
                text-color="white"
                >{{ item.status | pascalToReadable }}</v-chip
              >
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-if="item.isValid != undefined"
                    class="mx-2"
                    large
                    :color="item.isValid ? 'success' : 'warning'"
                    v-bind="attrs"
                    v-on="on"
                    >{{
                      item.isValid
                        ? "mdi-check-circle-outline"
                        : "mdi-alert-circle-outline"
                    }}</v-icon
                  >
                </template>
                <span>{{
                  item.isValid
                    ? "Passed Validation Checks"
                    : "Validation Errors Found"
                }}</span>
              </v-tooltip>
              <SyncStateIcon
                v-if="anyUnsyncedItems"
                :isItemUnsynced="isVoyageUnsynced(item)"
              />
            </div>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon :size="iconSize" @click="goToVoyageDetails(item)"
              >mdi-information</v-icon
            >
          </template>
        </v-data-table>
      </v-tab-item>
    </v-tabs-items>
  </v-container>
</template>

<script>
import Vue from 'vue';
import { mapState, mapGetters } from 'vuex';
import StatusColourMixin from '../mixins/StatusColourMixin';
import ResponsiveSizeHelperMixin from '../mixins/ResponsiveSizeHelperMixin';
import VoyageReportForm from '../components/voyage-reports/VoyageReportForm.vue';
import PageHeader from '../components/ui/PageHeader.vue';
import SyncStateIcon from '../components/ui/SyncStateIcon.vue';
import VoyageReportsHelperMixin from '../mixins/VoyageReportsHelperMixin';
import VoyageListCard from '../components/voyage-reports/VoyageListCard.vue';
import InfoAlert from '../components/ui/InfoAlert.vue';

export default {
  name: 'VoyageReports',
  components: {
    VoyageReportForm,
    PageHeader,
    SyncStateIcon,
    VoyageListCard,
    InfoAlert,
  },
  mixins: [StatusColourMixin, ResponsiveSizeHelperMixin, VoyageReportsHelperMixin],
  data() {
    return {
      voyageReportsLoading: false,
      isSubmitting: false,
      headers: [
        { text: 'Voyage No', value: 'voyageNo' },
        { text: 'Vessel', value: 'vesselName' },
        { text: 'Start Date Time (LT)', value: 'voyageStartDateTimeLt' },
        { text: 'Initial Port', value: 'initialPort' },
        { text: 'Status', value: 'status' },
        { text: 'Actions', align: 'end', value: 'actions' },
      ],
      search: '',
      showSearch: false,
      bus: new Vue(),
      dialog: false,
      report: {},
      defaultReport: {
        isNewVesselHirePeriod: false,
        status: 'Pending',
      },
    };
  },
  computed: {
    ...mapState(['voyageReportsStore', 'userStore', 'dataSyncStore']),
    ...mapGetters({
      getSyncedAndUnSyncedVoyageReports: 'voyageReportsStore/getSyncedAndUnSyncedVoyageReports',
      getAllUnSyncedVesselActivities: 'dataSyncStore/getAllUnSyncedVesselActivities',
      isClientConnectedToServer: 'userStore/isClientConnectedToServer',
      getActiveSite: 'userStore/getActiveSite',
    }),
    tab: {
      set(tab) {
        this.$router.replace({ query: { ...this.$route.query, tab } });
      },
      get() {
        return this.$route.query.tab;
      },
    },
    title() {
      return this.tab === 'active' ? 'Active Voyages' : 'Voyages';
    },
    voyageReports() {
      return this.getSyncedAndUnSyncedVoyageReports ?? [];
    },
    activeVoyageReports() {
      return this.voyageReports.filter((x) => x.status !== 'Completed');
    },
    activeSiteId() {
      const site = this.getActiveSite;
      if (!site) {
        return null;
      }
      return site.id;
    },
    centerTabs() {
      return this.$vuetify.breakpoint.xs
      || this.$vuetify.breakpoint.sm
      || this.$vuetify.breakpoint.md;
    },
  },
  watch: {
    activeSiteId(newVal) {
      this.loadVoyageReports();
      this.$store.dispatch('vesselsStore/loadVessels', this.activeSiteId);
      this.$store.dispatch('locationsStore/loadVesselLocations', this.activeSiteId);
    },
  },
  created() {
    this.report = { ...this.defaultReport };
    this.loadVoyageReports();
    this.$store.dispatch('dataSyncStore/loadAllVesselActivitiesToBeSynced');
    this.$store.dispatch('vesselsStore/loadVessels', this.activeSiteId);
    this.$store.dispatch('locationsStore/loadVesselLocations', this.activeSiteId);
  },
  methods: {
    loadVoyageReports() {
      this.voyageReportsLoading = true;
      this.$store.dispatch('voyageReportsStore/loadVoyageReports', this.activeSiteId)
        .then(() => this.$store.dispatch('dataSyncStore/loadVoyageReportsToBeSynced'))
        .finally(() => { this.voyageReportsLoading = false; });
    },
    close() {
      this.report = { ...this.defaultReport };
      this.bus.$emit('resetForm');
      this.dialog = false;
    },
    submitVoyageReport() {
      this.createVoyageReport();
    },
    createVoyageReport() {
      this.isSubmitting = true;
      this.report.siteId = this.activeSiteId;

      const reportModel = {
        ...this.report,
        siteId: this.activeSiteId,
        initialPort: this.report.initialPortLocation.name,
      };

      this.$store.dispatch('voyageReportsStore/createVoyageReport', reportModel)
        .then((resp) => { this.onSuccess('Voyage Report Created', resp); })
        .catch((err) => {
          this.onError(err.message);
          this.bus.$emit('resetForm');
          console.error(err);
        })
        .finally(() => { this.isSubmitting = false; });
    },
    customRowClass(item) {
      let rowClass = '';
      const anyUnsyncedActivitiesForVoyage = this.isAnyUnsyncedActivitiesForVoyage(item);

      if ((this.anyUnsyncedItems) && item) {
        rowClass = this.isVoyageUnsynced(item) ? 'green lighten-4' : 'blue-grey lighten-4';
      }
      return rowClass;
    },
    onSuccess(message, data) {
      this.$toast.success(message);
      this.close();
    },
    onError(message, data) {
      this.$toast.error(message);
    },
  },
};
</script>
